import { s_subscribe, s_unsubscribe } from "badmfck-signal";
import React, { useEffect, useRef, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import styled, { keyframes } from "styled-components";
import { Box } from "../box/Box";
import { InputBoxStyle, InputFontStyle } from "../input/Input";
import Spacer from "../spacer/Spacer";
import { Typo, TypoCaption } from "../text/Typo";
import { useWindowSize } from "../utils/useResize";
import { registerSystemBackOverlay } from "../utils/systemBack";

export interface ISelectItemProps<T> {
	value: T | null;
	prompt?: boolean;
	selected?: boolean;
	size?: "small" | "medium" | "large";
	variant?: "default" | "reference";
}

export type SelectValue<T> = T extends { value: infer V } ? V : T;

interface ISelectProps<T = any> {
	id?: string;
	sx?: React.CSSProperties & { [key: string]: any };
	ignoreInput?: boolean;
	label?: string;
	ariaLabel?: string;
	sheetLabel?: string;
	name?: string;
	data?: Record<string, any>;
	value?: T | SelectValue<T>;
	item?: React.ReactElement<ISelectItemProps<T>>;
	values?: readonly T[];
	children?: React.ReactElement<ISelectItemProps<T>>;
	onChange?: (v: SelectValue<T>) => void;
	sheetAlign?: "right" | "left";
	sheetOffset?: number;
	minSheetWidth?: string;
	sheetWidth?: string;
	simple?: boolean;
	bg?: string;
	readOnly?: boolean;
	disabled?: boolean;
	size?: "small" | "medium" | "large";
	variant?: "default" | "reference";
	flex?: string;
	searchable?: boolean;
	searchPlaceholder?: string;
	searchMode?: "contains" | "prefix";
}

const animationDown = keyframes`
    0% {
      transform: translateY(-8px);
      opacity: 0;
    }
    100% {
      transform: translateY(0px);
      opacity: 1;
    }
`;

const animationHideDown = keyframes`
    0% {
      transform: translateY(0px);
      opacity: 1;
    }

    100% {
      transform: translateY(-8px);
      opacity: 0;
    }

`;

const animationUp = keyframes`
    0% {
      transform: translateY(8px);
      opacity: 0;
    }
    100% {
      transform: translateY(0px);
      opacity: 1;
    }
`;

const animationHideUp = keyframes`
    0% {
      transform: translateY(0px);
      opacity: 1;
    }
    100% {
      transform: translateY(8px);
      opacity: 0;
    }
`;

const SelectDiv = styled.div<{
	$sheetAlign?: string;
	$minSheetWidth?: string;
	$bg?: string;
	$sheetWidth?: string;
	$opened: boolean;
	$openUp?: boolean;
	$variant?: "default" | "reference";
}>`
	position: fixed;
	display: flex;
	flex-direction: column;
	border-radius: ${props => (props.$variant === "reference" ? "8px" : "12px")};
	overflow: hidden;
	max-height: ${props => (props.$variant === "reference" ? "38vh" : "30vh")};
	z-index: 100001;
	border: ${props => (props.$variant === "reference" ? "1px solid #dccfc7" : "1px solid #D8E0EA")};
	padding: ${props => (props.$variant === "reference" ? "4px" : "8px")};

	${props => (props.$minSheetWidth ? "min-width:" + props.$minSheetWidth : "")};
	${props => (props.$sheetWidth ? "width:" + props.$sheetWidth : "")};
	${props => (props.$bg ? "background-color:" + props.$bg : "background-color:#FFFFFF")};

	&[data-state="hide"] {
		animation: ${props => (props.$openUp ? animationHideUp : animationHideDown)} 0.24s cubic-bezier(.22, 1, .36, 1);
		transform: translateY(${props => (props.$openUp ? "8px" : "-8px")});
		opacity: 0;
	}
	&[data-state="dispose"] {
		animation: ${props => (props.$openUp ? animationHideUp : animationHideDown)} 0.24s cubic-bezier(.22, 1, .36, 1);
		transform: translateY(${props => (props.$openUp ? "8px" : "-8px")});
		opacity: 0;
		display: none;
	}
	&[data-state="show"] {
		animation: ${props => (props.$openUp ? animationUp : animationDown)} 0.24s cubic-bezier(.22, 1, .36, 1);
	}

	box-shadow: ${props => (props.$variant === "reference" ? "0 12px 28px rgba(59, 43, 32, 0.16)" : "0 18px 42px rgba(15, 23, 42, 0.18), 0 4px 12px rgba(15, 23, 42, 0.08)")};
`;

const SearchInput = styled.input`
	width: 100%;
	height: 40px;
	box-sizing: border-box;
	border: 1px solid #cbd5e1;
	border-radius: 8px;
	padding: 0 12px;
	background: #ffffff;
	color: #0f172a;
	font: inherit;
	font-size: 14px;
	outline: none;

	&::placeholder {
		color: #64748b;
	}

	&:focus {
		border-color: #64748b;
		box-shadow: 0 0 0 3px rgba(100, 116, 139, 0.14);
	}
`;

const getDisplayName = (value: any) => {
	let displayValue = value ? "???" : "---";

	if ((typeof value === "string" && value) || typeof value === "number") displayValue = value + "";

	if (typeof value === "boolean") displayValue = value ? "YES" : "NO";

	if (typeof value === "object") {
		if (value && value.hasOwnProperty("name")) displayValue = (value as any)["name"];
		if (value && value.hasOwnProperty("label")) displayValue = (value as any)["label"];
		else if (value && value.hasOwnProperty("title")) displayValue = (value as any)["title"];
		else if (value && value.hasOwnProperty("displayName")) displayValue = (value as any)["displayName"];
	}

	return displayValue;
};

export const Select = <T,>({
	id,
	sx,
	simple,
	sheetAlign,
	bg,
	minSheetWidth,
	sheetWidth,
	label,
	ariaLabel,
	sheetLabel,
	name,
	data,
	item,
	values,
	onChange,
	value,
	children,
	disabled,
	readOnly,
	size,
	variant = "default",
	flex,
	sheetOffset = 0,
	searchable = false,
	searchPlaceholder = "Search by name or code...",
	searchMode = "contains",
}: ISelectProps<T>) => {
	const [selectedValue, setValue] = useState<T | SelectValue<T> | null>(data && name ? data[name] : (value ?? null));
	const [state, setState] = useState<"show" | "hide" | "dispose">("dispose");
	const [_id] = useState<string>(id ? id : +new Date() + "" + Math.random() * 10000);
	const listboxId = `${_id}-listbox`;
	const triggerRef = useRef<HTMLDivElement>(null);
	const controlRef = useRef<HTMLElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const searchInputRef = useRef<HTMLInputElement>(null);
	const optionRefs = useRef<Array<HTMLElement | null>>([]);
	const openFocusEdge = useRef<"first" | "last">("first");
	const tidRef = useRef<number>(0);
	const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number; openUp: boolean }>({ top: 0, left: 0, width: 0, openUp: false });
	const windowSize = useWindowSize();
	const isMobile = windowSize.width <= 768;
	const [sheetVisible, setSheetVisible] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	if (!size) size = "medium";

	const effect = data ? [data[name ?? 1] ?? 1] : [];
	useEffect(() => {
		setValue(data && name ? data[name] : value);
	}, [effect]);

	const iconTsx: Record<string, any> = {};
	if (state == "show") {
		iconTsx.transform = "rotate(180deg)";
	}

	const updatePosition = useCallback(() => {
		if (!triggerRef.current) return;
		const rect = triggerRef.current.getBoundingClientRect();
		const maxDropdownHeight = window.innerHeight * 0.3; // matches max-height: 30vh
		const spaceBelow = window.innerHeight - rect.bottom;
		const spaceAbove = rect.top;
		const openUp = spaceBelow < maxDropdownHeight && spaceAbove > spaceBelow;
		setDropdownPos({
			top: openUp ? rect.top : rect.bottom,
			left: sheetAlign === "right" ? rect.right : rect.left,
			width: rect.width,
			openUp,
		});
	}, [sheetAlign]);

	const hide = () => {
		setSearchQuery("");
		if (isMobile) {
			setSheetVisible(false);
			clearTimeout(tidRef.current);
			tidRef.current = setTimeout(() => {
				setState("dispose");
			}, 300) as any;
			return;
		}
		clearTimeout(tidRef.current);
		setState("hide");
		tidRef.current = setTimeout(() => {
			setState("dispose");
		}, 200) as any;
	};

	const show = () => {
		if (disabled || readOnly) return;
		if (isMobile) {
			clearTimeout(tidRef.current);
			setState("show");
			requestAnimationFrame(() => setSheetVisible(true));
			return;
		}
		clearTimeout(tidRef.current);
		updatePosition();
		setState("show");
	};
	const hideRef = useRef(hide);
	hideRef.current = hide;

	useEffect(() => {
		if (state !== "show") return;
		return registerSystemBackOverlay(() => hideRef.current());
	}, [state]);

	const focusOption = (direction: 1 | -1, edge?: "first" | "last") => {
		const enabledOptions = optionRefs.current.filter(
			(option): option is HTMLElement => Boolean(option && !option.hasAttribute("disabled"))
		);
		if (enabledOptions.length === 0) return;
		if (edge) {
			enabledOptions[edge === "first" ? 0 : enabledOptions.length - 1].focus();
			return;
		}
		const currentIndex = enabledOptions.indexOf(document.activeElement as HTMLElement);
		const nextIndex =
			currentIndex < 0
				? direction === 1
					? 0
					: enabledOptions.length - 1
				: (currentIndex + direction + enabledOptions.length) % enabledOptions.length;
		enabledOptions[nextIndex].focus();
	};

	useEffect(() => {
		if (state !== "show") return;
		if (searchable) {
			const frame = requestAnimationFrame(() => searchInputRef.current?.focus());
			return () => cancelAnimationFrame(frame);
		}
		const edge = openFocusEdge.current;
		openFocusEdge.current = "first";
		const frame = requestAnimationFrame(() => focusOption(1, edge));
		return () => cancelAnimationFrame(frame);
	}, [state, searchable]);

	// Track scroll on all ancestor elements to reposition or close dropdown
	useEffect(() => {
		if (isMobile) return;
		if (state !== "show") return;
		const handleScroll = () => {
			if (!triggerRef.current) return;
			const rect = triggerRef.current.getBoundingClientRect();
			// Close if trigger scrolled out of viewport
			if (rect.bottom < 0 || rect.top > window.innerHeight) {
				hide();
				return;
			}
			updatePosition();
		};
		window.addEventListener("scroll", handleScroll, true);
		window.addEventListener("resize", handleScroll);
		return () => {
			window.removeEventListener("scroll", handleScroll, true);
			window.removeEventListener("resize", handleScroll);
		};
	}, [state, updatePosition]);

	useEffect(() => {
		if (data && name && data[name]) {
			return;
		}
		setValue(value ?? null);
	}, [value]);

	useEffect(() => {
		const evt = (e: MouseEvent) => {
			const target = e.target as HTMLElement;
			const inTrigger = triggerRef.current?.contains(target);
			const inDropdown = dropdownRef.current?.contains(target);

			if (!inTrigger && !inDropdown) {
				if (state === "show") hide();
			}
		};

		if (state === "show" && !isMobile) window.addEventListener("mousedown", evt);

		const signalID = s_subscribe(() => {
			if (state === "show") hide();
			else show();
		}, "TOGGLE_SELECT_" + _id);

		return () => {
			window.removeEventListener("mousedown", evt);
			s_unsubscribe(signalID);
		};
	});

	let iconSize = "40px";
	if (size === "small") iconSize = "30px";
	if (size === "large") iconSize = "50px";
	if (variant === "reference") iconSize = "26px";

	const icon = !simple && (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				cursor: "pointer",
				width: iconSize,
				"&:hover>svg>path": {
					fill: "#5d6e97",
				},
				"&>svg": {
					pointerEvents: "none",
					height: variant === "reference" ? "18px" : "24px",
					width: variant === "reference" ? "18px" : "24px",
				},
				transition: "transform .3s",
				...iconTsx,
			}}>
			<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
				<path d="M17 9V11L12 16L7 11V9L12 14L17 9Z" fill={variant === "reference" ? "#6b6762" : "#000000CC"} fillOpacity="1" />
			</svg>
		</Box>
	);

	const handleValueSelect = (option: T) => {
		const nextValue =
			typeof option === "object" && option !== null && "value" in option
				? (option.value as SelectValue<T>)
				: (option as SelectValue<T>);

		if (name && data) data[name] = nextValue;
		setValue(nextValue);
		if (onChange) onChange(nextValue);
		hide();
		requestAnimationFrame(() => controlRef.current?.focus());
	};

	let itm = null;
	if (children) itm = children;
	if (!itm && item) itm = item;

	// find selected item
	let _selectedValue = null;
	if (values && selectedValue && typeof selectedValue !== "object") {
		for (let i of values) {
			if (typeof i === "object") {
				for (let j in i) {
					if (i[j] === selectedValue) {
						_selectedValue = i;
						break;
					}
				}
			}
		}
	}
	if (!_selectedValue) _selectedValue = selectedValue;

	const hasNoOptions = Array.isArray(values) && values.length === 0;
	const selected = hasNoOptions
		? <SelectItem prompt size={size} value={"No options available" as T} variant={variant} />
		: itm
			? React.cloneElement(itm, { value: _selectedValue as T, prompt: true, variant })
			: <SelectItem prompt size={size} value={_selectedValue} variant={variant} />;

	const s = state === "dispose" ? "hide" : state;
	const dropdownStyle: React.CSSProperties = {
		width: sheetWidth || dropdownPos.width,
		...(dropdownPos.openUp
			? { bottom: window.innerHeight - dropdownPos.top + sheetOffset }
			: { top: dropdownPos.top + sheetOffset }),
		...(sheetAlign === "right" ? { right: window.innerWidth - dropdownPos.left } : { left: dropdownPos.left }),
	};

	const normalizedSearch = searchQuery.trim().toLocaleLowerCase();
	const filteredValues = values?.filter(value => {
		if (!normalizedSearch) return true;
		const displayName = String(getDisplayName(value)).toLocaleLowerCase();
		const displayMatches = searchMode === "prefix"
			? displayName.startsWith(normalizedSearch)
			: displayName.includes(normalizedSearch);
		if (displayMatches) return true;

		if (typeof value !== "object" || value === null) return false;
		return Object.entries(value).some(([key, part]) =>
			!["name", "label", "title", "displayName"].includes(key) &&
			String(part ?? "").toLocaleLowerCase().includes(normalizedSearch)
		);
	});

	const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "ArrowDown" || event.key === "ArrowUp") {
			event.preventDefault();
			focusOption(event.key === "ArrowDown" ? 1 : -1, event.key === "ArrowDown" ? "first" : "last");
			return;
		}
		if (event.key === "Escape") {
			event.preventDefault();
			event.stopPropagation();
			hide();
			requestAnimationFrame(() => controlRef.current?.focus());
		}
	};

	const searchBox = searchable ? (
		<Box sx={{ padding: "4px 4px 8px", flexShrink: 0 }}>
			<SearchInput
				ref={searchInputRef}
				type='search'
				aria-label={label ? `Search ${label}` : "Search options"}
				placeholder={searchPlaceholder}
				value={searchQuery}
				onChange={event => setSearchQuery(event.target.value)}
				onKeyDown={handleSearchKeyDown}
				onClick={event => event.stopPropagation()}
			/>
		</Box>
	) : null;

	const renderItems = () => {
		if (!filteredValues?.length) {
			return (
				<Box role='status' sx={{ padding: "16px 12px", textAlign: "center", color: "#64748B" }}>
					<Typo sx={{ color: "inherit", fontSize: "14px" }}>
						{searchable && normalizedSearch ? "No matches found" : "No options available"}
					</Typo>
				</Box>
			);
		}

		return filteredValues.map((v: T, index: number) => {
			const props = {
				value: v,
				selected: v === selectedValue,
			};

			if (typeof v === "object" && typeof selectedValue !== "object") {
				for (let i in v) {
					if (v[i] === selectedValue) {
						props.value = v;
						props.selected = true;
						break;
					}
				}
			}

			const i = item ? React.cloneElement(item, { ...props, variant }) : <SelectItem size={isMobile ? "large" : size} variant={variant} {...props} />;
			return (
				<Box
					element='button'
					type='button'
					disabled={props.selected}
					role='option'
					aria-selected={props.selected}
					key={index}
					ref={node => {
						optionRefs.current[index] = node as HTMLElement | null;
					}}
					sx={{
						width: "100%",
						border: "none",
						backgroundColor: "transparent",
						fontFamily: "inherit",
						textAlign: "left",
						cursor: props.selected ? "auto" : "pointer",
						borderBottom: "1px solid #EEF2F7",
						"&:last-child": {
							borderBottom: "none",
						},
					}}
					onClick={e => {
						e!.preventDefault();
						e!.stopPropagation();
						if (state !== "show") return;
						handleValueSelect(v);
					}}
					onKeyDown={e => {
						if (e.key === "ArrowDown" || e.key === "ArrowUp") {
							e.preventDefault();
							focusOption(e.key === "ArrowDown" ? 1 : -1);
							return;
						}
						if (e.key === "Home" || e.key === "End") {
							e.preventDefault();
							focusOption(1, e.key === "Home" ? "first" : "last");
							return;
						}
						if (e.key === "Escape") {
							e.preventDefault();
							e.stopPropagation();
							hide();
							requestAnimationFrame(() => controlRef.current?.focus());
							return;
						}
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							e.stopPropagation();
							if (state === "show") handleValueSelect(v);
						}
					}}>
					{i}
				</Box>
			);
		});
	};

	const optionList = (mobile = false) => (
		<Box
			id={mobile ? listboxId : undefined}
			role={mobile ? "listbox" : undefined}
			sx={{
				overflow: "auto",
				WebkitOverflowScrolling: "touch",
				padding: mobile
					? "0 8px max(16px, var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))"
					: "0",
				minHeight: 0
			}}>
			{renderItems()}
		</Box>
	);

	const mobileSheet =
		isMobile &&
		state !== "dispose" &&
		ReactDOM.createPortal(
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					zIndex: 100001,
					display: "flex",
					alignItems: "flex-end",
					justifyContent: "center",
					backgroundColor: sheetVisible ? "rgba(0,0,0,0.5)" : "rgba(0,0,0,0)",
					transition: "background-color 0.3s ease",
				}}
				onClick={e => {
					e?.preventDefault();
					e?.stopPropagation();
					hide();
				}}>
				<Box
					sx={{
						width: "100%",
						minHeight: "25vh",
						maxHeight: "min(70dvh, calc(100dvh - var(--safe-area-inset-top, env(safe-area-inset-top, 0px))))",
						backgroundColor: bg || "#FFFFFF",
						borderRadius: "16px 16px 0 0",
						transform: sheetVisible ? "translateY(0)" : "translateY(100%)",
						transition: "transform 0.3s ease",
						display: "flex",
						flexDirection: "column",
						overflow: "hidden",
					}}
					onClick={e => {
						e!.stopPropagation();
					}}>
					{/* Drag handle */}
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							padding: "10px 0 6px",
						}}>
						<Box
							sx={{
								width: "36px",
								height: "4px",
								borderRadius: "2px",
								backgroundColor: "rgba(0,0,0,0.2)",
							}}
						/>
					</Box>
					{(sheetLabel || label) && (
						<Box sx={{ padding: "0 16px 8px", borderBottom: "1px solid rgba(0,0,0,0.1)" }}>
							<Typo sx={{ fontWeight: 700, fontSize: "1rem" }}>{sheetLabel || label}</Typo>
						</Box>
					)}
					<Box sx={{ padding: "6px 8px 0" }}>{searchBox}</Box>
					{optionList(true)}
				</Box>
			</Box>,
			document.body,
		);

	return (
		<Box
			sx={{
				position: "relative",
				flex: flex ?? undefined,
			}}
			ref={triggerRef}
			id={_id}>
			{label && (
				<>
					<TypoCaption block sx={{ color: "#536173", fontWeight: 700, letterSpacing: 0 }}>
						{label}
					</TypoCaption>
					<Spacer size="7px" />
				</>
			)}

			<Box
				ref={controlRef}
				data-input-control
				role='combobox'
				tabIndex={disabled || readOnly ? -1 : 0}
				aria-label={ariaLabel || label}
				aria-expanded={state === "show"}
				aria-controls={listboxId}
				aria-haspopup='listbox'
				aria-disabled={disabled || readOnly}
				sx={{
					display: "grid",
					gridTemplateColumns: variant === "reference" ? "1fr 26px" : "1fr max-content",

					/*border:"1px solid rgba(127,116,116,.1)",
                boxSizing:"border-box",
                boxShadow:"0px 0px 20px rgba(0,0,0,.1)",*/

					...InputBoxStyle(false),

					borderRadius: "10px",
					cursor: disabled || readOnly ? "default" : "pointer",
					overflow: "hidden",
					position: "relative",
					backgroundColor: bg ? bg : "#FFFFFF",
					transition: "border-color .18s ease, box-shadow .18s ease, background-color .18s ease",

					"&:hover": {
						border: "1px solid #94A3B8",
						boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)",
					},

					...(variant === "reference"
						? {
								minHeight: "30px",
								height: "30px",
								border: "1px solid #d8cfc8",
								borderRadius: "8px",
								backgroundColor: bg ? bg : "#fbfaf8",
								boxShadow: "inset 0 1px 0 rgba(255,255,255,.75)",
								"&:hover": {
									border: "1px solid #bfaea3",
									backgroundColor: "#ffffff",
									transition: "border .2s, background-color .2s",
								},
						  }
						: {}),

					...sx,
				}}
				onClick={e => {
					if (!e) return;
					e.preventDefault();
					e.stopPropagation();
					if (state === "show") hide();
					else show();
				}}
				onKeyDown={e => {
					if (e.key === "ArrowDown" || e.key === "ArrowUp") {
						e.preventDefault();
						if (state !== "show") {
							openFocusEdge.current = e.key === "ArrowUp" ? "last" : "first";
							show();
						}
						else focusOption(e.key === "ArrowDown" ? 1 : -1);
						return;
					}
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						if (state === "show") hide();
						else show();
					}
					if (e.key === "Escape" && state === "show") {
						e.preventDefault();
						e.stopPropagation();
						hide();
					}
				}}>
				{selected}
				{icon}
			</Box>

			{isMobile
				? mobileSheet
				: state !== "dispose" &&
					ReactDOM.createPortal(
						<SelectDiv
							id={listboxId}
							role='listbox'
							ref={dropdownRef}
							$opened={s === "show"}
							$openUp={dropdownPos.openUp}
							data-state={s}
							$bg={bg}
							$sheetAlign={sheetAlign}
							$sheetWidth={sheetWidth}
							$minSheetWidth={minSheetWidth}
							$variant={variant}
							style={dropdownStyle}>
							{searchBox}
							{optionList()}
						</SelectDiv>,
						document.body,
					)}
		</Box>
	);
};

const SelectItem = <T,>({ value, prompt, selected, size, variant = "default" }: ISelectItemProps<T>) => {
	const empty = value === undefined || value === null || value === "";
	let displayValue = empty ? (prompt ? "Select…" : "—") : getDisplayName(value);
	const badge =
		typeof value === "object" && value !== null && "badge" in value
			? String((value as { badge: unknown }).badge)
			: null;
	const tsx: Record<string, any> = {};
	if (empty && prompt) tsx.color = "#94A3B8";
	if (!size) size = "medium";
	if (variant === "reference") {
		tsx.height = prompt ? "28px" : "30px";
		tsx.padding = "0 10px";
		tsx.display = "flex";
		tsx.alignItems = "center";
		tsx.color = "#5f5a56";
		tsx.fontSize = "11px";
		tsx.fontWeight = 600;
		tsx.letterSpacing = "0.08em";
		tsx.textTransform = "uppercase";
		tsx.borderRadius = prompt ? "0" : "6px";
	}
	if (!prompt) {
		tsx["&:hover"] = { backgroundColor: variant === "reference" ? "#f8f0ed" : "#F8FAFC" };
		((tsx.borderBottom = "none"), (tsx.transition = "background-color .18s ease, color .18s ease"));
		tsx.borderRadius = variant === "reference" ? tsx.borderRadius : "7px";
	}

	if (selected) {
		tsx.pointerEvents = "none";
		tsx.backgroundColor = variant === "reference" ? "#f8f0ed" : "#EEF4FF";
		tsx.color = variant === "reference" ? "#1f1d1b" : "#1D4ED8";
		tsx.borderRadius = variant === "reference" ? tsx.borderRadius : "7px";
	}

	return (
		<Box
			sx={{
				...InputFontStyle(size),
				...tsx,
				overflow: "hidden",
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				gap: "10px",
			}}>
			<Typo
				sx={{
					fontSize: variant === "reference" ? "11px" : undefined,
					fontWeight: variant === "reference" ? 600 : undefined,
					letterSpacing: variant === "reference" ? "0.08em" : undefined,
					textTransform: variant === "reference" ? "uppercase" : undefined,
					textOverflow: "ellipsis",
					overflow: "hidden",
					whiteSpace: "nowrap",
				}}>
				{displayValue}
			</Typo>
			{badge && !prompt && (
				<Box
					sx={{
						flexShrink: 0,
						padding: "2px 7px",
						borderRadius: "999px",
						border: "1px solid #FDBA74",
						backgroundColor: "#FFF7ED",
					}}>
					<Typo sx={{ color: "#9A3412", fontSize: "0.68rem", fontWeight: 700, whiteSpace: "nowrap" }}>
						{badge}
					</Typo>
				</Box>
			)}
		</Box>
	);
};
